import { useState, useEffect, useCallback } from 'react';
import { Check, Copy, KeyRound, Plus, Terminal } from 'lucide-react';
import { useGroup } from '../contexts/GroupContext';
import { Button, ConfirmDialog, useToast } from '../components/common';
import api from '../services/api';
import { copyText } from '../utils/clipboard';
import './TokensPage.css';
import PortalPage from '../components/PortalPage';

/** A secret shown in a field with its own copy button; `display` is what is drawn when it differs. */
function CopyField({ label, value, display, copied, onCopy }) {
  return (
    <div className="tokens-field">
      <span className="tokens-field__label">{label}</span>
      <div className="tokens-field__box">
        <code className="tokens-field__value">{display ?? value}</code>
        <button type="button" className={`tokens-field__copy${copied ? ' is-done' : ''}`} onClick={onCopy}>
          {copied ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

export default function TokensPage({ embedded = false }) {
  const { activeGroupId: groupId, activeGroup, groups } = useGroup();
  const space = groupId ? (activeGroup?.name || 'this group') : 'Personal';
  const toast = useToast();

  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  // The token just minted: its value exists in this browser tab and nowhere else.
  const [fresh, setFresh] = useState(null); // { name, value }
  const [copied, setCopied] = useState({});
  const [creating, setCreating] = useState(false);
  const [tokenName, setTokenName] = useState('');
  const [expiryDays, setExpiryDays] = useState(90);
  const [confirm, setConfirm] = useState(null); // { kind: 'revoke'|'dismiss', token? }
  // The mint form opens on request, as in the mockup; with no tokens yet it is open.
  const [minting, setMinting] = useState(false);

  const loadTokens = useCallback(async () => {
    try {
      const data = await api.listDeveloperTokens();
      setTokens(data.tokens || []);
      setLoadError(null);
    } catch (err) {
      setLoadError(`${err?.status || '?'}: ${err?.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTokens(); }, [loadTokens]);

  // Leaving the page with a token nobody copied loses it for good, so the
  // browser asks first. Once anything was copied, it lets you go.
  const unsaved = Boolean(fresh) && !copied.token && !copied.login;
  useEffect(() => {
    if (!unsaved) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [unsaved]);

  const handleCreate = async () => {
    const name = tokenName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const data = await api.createDeveloperToken(name, expiryDays, groupId || null);
      setFresh({ name, value: data.token });
      setMinting(false);
      setCopied({});
      setTokenName('');
      setExpiryDays(90);
      await loadTokens();
    } catch (err) {
      toast.error(err.message || 'Failed to create token.');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (t) => {
    try {
      await api.revokeDeveloperToken(t.id);
      toast.success(`Token "${t.name}" revoked.`);
      setTokens(prev => prev.map(x => (x.id === t.id ? { ...x, is_revoked: true } : x)));
      loadTokens();
    } catch (err) {
      toast.error(err.message || 'Failed to revoke token.');
    }
  };

  // Copied only once the clipboard took it: the token cannot be shown again,
  // so a copy that silently failed must not unlock "Done".
  const copy = async (key, text) => {
    if (await copyText(text)) setCopied(c => ({ ...c, [key]: true }));
    else toast.error('Could not copy. Select the text and copy it by hand.');
  };

  // Each token names its own group, not whichever one is active now.
  const spaceOf = (t) => (t.group_id
    ? ((groups || []).find(g => String(g.id) === String(t.group_id))?.name || 'Group')
    : 'Personal');

  const closeFresh = () => { setFresh(null); setCopied({}); };

  const loginCmd = (value) => (groupId
    ? `paradigm login --token ${value} --group-id ${groupId}`
    : `paradigm login --token ${value}`);

  const active = tokens.filter(t => !t.is_revoked && !t.is_expired);
  // Asked for, or nothing to list yet: either way the form is the next step.
  const formOpen = minting || (!loading && !loadError && active.length === 0 && !fresh);
  const inactive = tokens.filter(t => t.is_revoked || t.is_expired);

  // Embedded (a tab of Start developing): same content, no page chrome.
  const Frame = embedded ? EmbeddedFrame : PortalPage;
  return (
    <Frame
      className="tokens-page"
      title="Developer tokens"
      lede={`For the CLI and CI. A token acts as you, inside ${space}, until it expires.`}
      actions={embedded ? null : (
        <Button onClick={() => setMinting(true)}><Plus size={14} aria-hidden="true" /> Mint token</Button>
      )}
    >
      {/* What a token can reach. Enforced, not advisory: identity data answers
          403 DEV_PAT_NOT_ALLOWED, and a developer who hits it needs to know it is
          the rule and which credential to use instead. */}
      <div className="tokens-scope">
        <div className="tokens-scope__card">
          <span className="tokens-scope__label">Can</span>
          <ul>
            <li>Register and edit apps and plugins</li>
            <li>Create and version schemas</li>
            <li>Declare a DLR</li>
            <li>Administer the groups that own them</li>
          </ul>
        </div>
        <div className="tokens-scope__card">
          <span className="tokens-scope__label">Cannot</span>
          <ul>
            <li>Read or write anyone&apos;s data, yours included</li>
            <li>Run a plugin against a person</li>
            <li>Change exposure profiles or privacy realms</li>
            <li>Touch the keys that decrypt a graph</li>
          </ul>
        </div>
      </div>
      <details className="tokens-why">
        <summary>Why it can&apos;t touch identity data</summary>
        <p>
          A developer token is not a way to act as a person. It carries no exposure profile, so
          nothing narrows what it reaches, and it is attributed to no application: a write made
          with one could not be traced back to anything but you. Reaching identity data returns{' '}
          <code>403 DEV_PAT_NOT_ALLOWED</code>. Use a <strong>user session</strong> or an{' '}
          <strong>app key</strong> with an exposure profile the user granted.
        </p>
      </details>

      {/* The token just made: on screen once, so copying it is the whole job. */}
      {fresh && (
        <div className="tokens-fresh" role="region" aria-label="Your new token">
          <div className="tokens-fresh__head">
            <span className="tokens-fresh__ic" aria-hidden="true"><KeyRound size={16} /></span>
            <div>
              <h2 className="tokens-band__title">“{fresh.name}” is ready</h2>
              <p className="tokens-band__help">
                Copy it now. Only a hash is kept, so it can&apos;t be shown again once you leave this page.
              </p>
            </div>
          </div>
          <CopyField label="Token" value={fresh.value} copied={copied.token}
                     onCopy={() => copy('token', fresh.value)} />
          <CopyField label="Sign in the CLI" value={loginCmd(fresh.value)}
                     copied={copied.login} onCopy={() => copy('login', loginCmd(fresh.value))} />
          <div className="tokens-fresh__foot">
            <span className="tokens-band__help">
              {copied.token || copied.login ? 'Saved somewhere safe? Then you are done.' : 'Nothing copied yet.'}
            </span>
            <Button variant={copied.token || copied.login ? 'primary' : 'secondary'}
                    onClick={() => (unsaved ? setConfirm({ kind: 'dismiss' }) : closeFresh())}>
              Done, I&apos;ve saved it
            </Button>
          </div>
        </div>
      )}

      <section className="tokens-sect">
        <div className="tokens-sect__head">
          <span className="tokens-sect__title">Active {active.length > 0 && <span className="tokens-band__count">{active.length}</span>}</span>
          {!formOpen && !fresh && (
            <button type="button" className="tokens-sect__act" onClick={() => setMinting(true)}>
              <Plus size={14} aria-hidden="true" /> Mint token
            </button>
          )}
        </div>

        {formOpen && (
          <div className="tokens-mint">
            <input
              type="text"
              className="tokens-input"
              placeholder="Which machine? e.g. MacBook Pro, or “ci”"
              aria-label="Token name"
              value={tokenName}
              onChange={e => setTokenName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') setMinting(false); }}
              maxLength={100}
              autoFocus={minting}
            />
            <select className="tokens-input tokens-input--select" aria-label="Expires after"
                    value={expiryDays} onChange={e => setExpiryDays(Number(e.target.value))}>
              <option value={30}>Expires in 30 days</option>
              <option value={90}>Expires in 90 days</option>
              <option value={180}>Expires in 180 days</option>
              <option value={365}>Expires in 365 days</option>
            </select>
            <Button onClick={handleCreate} disabled={creating || !tokenName.trim()}>
              {creating ? 'Creating…' : 'Create token'}
            </Button>
            {minting && active.length > 0 && (
              <button type="button" className="tokens-sect__act is-quiet" onClick={() => setMinting(false)}>Cancel</button>
            )}
          </div>
        )}

        {loading ? (
          <p className="tokens-band__help">Loading…</p>
        ) : loadError ? (
          <p className="tokens-band__help tokens-band__help--bad">Could not load tokens: {loadError}</p>
        ) : active.length > 0 && (
          <div className="tokens-list">
            {active.map(t => (
              <div key={t.id} className="tokens-row">
                <span className="tokens-row__ic" aria-hidden="true"><Terminal size={14} /></span>
                <div className="tokens-row__body">
                  <div><b>{t.name}</b> <code title="Only this preview is kept; the full token was shown once, when it was made.">{t.token_preview}</code></div>
                  <span>
                    {expiresIn(t.expires_at)} · {t.last_used_at ? `last used ${lastUsed(t.last_used_at)}` : 'never used'} · {spaceOf(t)}
                  </span>
                </div>
                <button type="button" className="tokens-row__revoke" onClick={() => setConfirm({ kind: 'revoke', token: t })}>
                  Revoke
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {inactive.length > 0 && (
        <details className="tokens-sect tokens-sect--quiet">
          <summary>Revoked and expired <span className="tokens-band__count">{inactive.length}</span></summary>
          <div className="tokens-list">
            {inactive.map(t => (
              <div key={t.id} className="tokens-row is-off">
                <span className="tokens-row__ic" aria-hidden="true"><Terminal size={14} /></span>
                <div className="tokens-row__body">
                  <div><b>{t.name}</b> <code>{t.token_preview}</code></div>
                  <span>{t.is_revoked ? 'Revoked' : 'Expired'} · {t.last_used_at ? `last used ${lastUsed(t.last_used_at)}` : 'never used'}</span>
                </div>
              </div>
            ))}
          </div>
        </details>
      )}

      <ConfirmDialog
        isOpen={Boolean(confirm)}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          if (confirm?.kind === 'revoke') handleRevoke(confirm.token);
          else closeFresh();
          setConfirm(null);
        }}
        title={confirm?.kind === 'revoke' ? `Revoke “${confirm?.token?.name}”?` : 'Close without copying?'}
        message={confirm?.kind === 'revoke'
          ? 'Anything signed in with this token stops working at once. This cannot be undone.'
          : 'You have not copied the token. Once this closes it can never be shown again, and you would need to make a new one.'}
        variant="danger"
        confirmLabel={confirm?.kind === 'revoke' ? 'Revoke' : 'Close anyway'}
        cancelLabel={confirm?.kind === 'revoke' ? 'Keep it' : 'Go back'}
      />
    </Frame>
  );
}

const DAY = 86400000;
/** "Expires in 21 days" — the distance is what a developer acts on, not the date. */
function expiresIn(iso) {
  const d = Math.ceil((new Date(iso).getTime() - Date.now()) / DAY);
  if (d <= 0) return 'Expires today';
  return d === 1 ? 'Expires tomorrow' : `Expires in ${d} days`;
}
/** "today", "yesterday", or the date. */
function lastUsed(iso) {
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / DAY);
  if (d <= 0) return 'today';
  if (d === 1) return 'yesterday';
  return new Date(iso).toLocaleDateString();
}

/** The same content with no page chrome, for use inside another page. */
function EmbeddedFrame({ className, children }) {
  return <div className={className}>{children}</div>;
}
